/**
 * starterPacks.ts — Curated preset selections for specialty onboarding
 *
 * Each starter pack pre-fills subspecialties, topics, and journals
 * for a given specialty. These are PRESENTATION presets only —
 * they do not change enforcement logic or the specialty catalog.
 *
 * The user can modify any pre-filled value before saving.
 * Starter packs are only offered during NEW profile creation, not edit.
 *
 * All IDs reference existing entries in specialty_config.json.
 */

export interface StarterPack {
  specialtyId: string;
  label: string;
  description: string;
  subspecialtyIds: string[];
  topics: string[];
  journals: string[];
}

/**
 * Curated starter packs for high-traffic specialties.
 * Each picks ~2 subspecialties, ~5 topics, ~5 journals — a balanced starting point.
 */
export const STARTER_PACKS: StarterPack[] = [
  {
    specialtyId: 'cardiology',
    label: 'Cardiology Quick Start',
    description: 'General cardiology + electrophysiology essentials',
    subspecialtyIds: ['cardiology_general_cardiology', 'cardiology_electrophysiology'],
    topics: [
      'Acute Coronary Syndromes (STEMI/NSTEMI)',
      'Heart Failure Management (GDMT)',
      'Atrial Fibrillation Management',
      'Valvular Heart Disease Assessment',
      'Atrial Fibrillation Ablation (PVI)',
    ],
    journals: [
      'J Am Coll Cardiol',
      'Circulation',
      'Eur Heart J',
      'JAMA Cardiol',
      'Heart Rhythm',
    ],
  },
  {
    specialtyId: 'internal_medicine',
    label: 'Internal Medicine Quick Start',
    description: 'Hospital medicine + critical care foundations',
    subspecialtyIds: ['internal_medicine_general_core', 'internal_medicine_critical_care_medicine'],
    topics: [
      'Hospital Medicine (CHF/Sepsis)',
      'Venous Thromboembolism (DVT/PE)',
      'Acute Kidney Injury Workup',
      'Sepsis & Septic Shock (Surviving Sepsis)',
      'ARDS & Mechanical Ventilation Strategies',
    ],
    journals: [
      'N Engl J Med',
      'Lancet',
      'JAMA',
      'Am J Respir Crit Care Med',
      'Ann Intern Med',
    ],
  },
  {
    specialtyId: 'neurology',
    label: 'Neurology Quick Start',
    description: 'General neurology + vascular neurology',
    subspecialtyIds: ['neurology_general_core', 'neurology_vascular_neurology'],
    topics: [
      'Acute Ischemic Stroke and TIA',
      'Epilepsy and First Seizure Workup',
      'Headache Disorders (Migraine/Cluster)',
      'Acute Reperfusion for Ischemic Stroke',
      'Secondary Stroke Prevention',
    ],
    journals: [
      'Lancet Neurol',
      'Neurology',
      'JAMA Neurol',
      'Stroke',
      'Ann Neurol',
    ],
  },
  {
    specialtyId: 'surgery_general',
    label: 'General Surgery Quick Start',
    description: 'Core surgery + trauma essentials',
    subspecialtyIds: ['surgery_general_general_core', 'surgery_general_trauma_surgery'],
    topics: [
      'Acute Abdomen',
      'Hernia Repair',
      'Trauma Surgery (ATLS)',
      'Damage Control Surgery',
      'Massive Transfusion Protocols',
    ],
    journals: [
      'Ann Surg',
      'JAMA Surg',
      'Br J Surg',
      'J Trauma Acute Care Surg',
      'J Am Coll Surg',
    ],
  },
  {
    specialtyId: 'pediatrics',
    label: 'Pediatrics Quick Start',
    description: 'General pediatrics + neonatal medicine',
    subspecialtyIds: ['pediatrics_general_core', 'pediatrics_neonatal_perinatal_medicine'],
    topics: [
      'Growth & Development Assessment',
      'Immunization Schedule',
      'Newborn Care & Resuscitation',
      'Neonatal Resuscitation Program (NRP)',
      'Prematurity Complications (RDS/BPD)',
    ],
    journals: [
      'JAMA Pediatr',
      'Pediatrics',
      'Lancet Child Adolesc Health',
      'J Pediatr',
      'Arch Dis Child',
    ],
  },
  {
    specialtyId: 'emergency_medicine',
    label: 'Emergency Medicine Quick Start',
    description: 'Core EM + toxicology basics',
    subspecialtyIds: ['emergency_medicine_general_core', 'emergency_medicine_medical_toxicology'],
    topics: [
      'Acute Coronary Syndromes',
      'Stroke Code Management (tPA/Thrombectomy)',
      'Sepsis Resuscitation',
      'Trauma (ATLS/E-FAST)',
      'Airway Management (RSI)',
    ],
    journals: [
      'Ann Emerg Med',
      'Resuscitation',
      'Acad Emerg Med',
      'Am J Emerg Med',
      'Clin Toxicol',
    ],
  },
  {
    specialtyId: 'obstetrics_and_gynecology',
    label: 'OB/GYN Quick Start',
    description: 'Core OB/GYN + maternal-fetal medicine',
    subspecialtyIds: ['obstetrics_and_gynecology_general_core', 'obstetrics_and_gynecology_maternal_fetal_medicine'],
    topics: [
      'Prenatal Care & Screening',
      'Labor Management & Delivery',
      'Obstetric Hemorrhage',
      'Preterm Labor Management',
      'Hypertensive Disorders of Pregnancy',
    ],
    journals: [
      'Am J Obstet Gynecol',
      'Obstet Gynecol',
      'BJOG',
      'Am J Obstet Gynecol MFM',
      'Hum Reprod Update',
    ],
  },
  {
    specialtyId: 'anesthesiology',
    label: 'Anesthesiology Quick Start',
    description: 'Core anesthesia + critical care',
    subspecialtyIds: ['anesthesiology_general_core', 'anesthesiology_critical_care_medicine'],
    topics: [
      'Airway Management Algorithms',
      'Neuromuscular Blockade & Reversal',
      'Hemodynamic Monitoring (TEE/Arterial)',
      'Sepsis Resuscitation Bundles',
      'ARDS Ventilation Strategies (Prone/Low TV)',
    ],
    journals: [
      'Anesthesiology',
      'Br J Anaesth',
      'Anesth Analg',
      'Am J Respir Crit Care Med',
      'Crit Care Med',
    ],
  },
  {
    specialtyId: 'dermatology',
    label: 'Dermatology Quick Start',
    description: 'Core derm + surgical oncology',
    subspecialtyIds: ['dermatology_general_core', 'dermatology_micrographic_surgery_and_oncology'],
    topics: [
      'Melanoma Staging & Management',
      'Psoriasis (Biologics/Pathophysiology)',
      'Atopic Dermatitis (JAK inhibitors)',
      'Basal Cell Carcinoma',
      'Mohs Appropriate Use Criteria (AUC)',
    ],
    journals: [
      'J Am Acad Dermatol',
      'JAMA Dermatol',
      'J Invest Dermatol',
      'Br J Dermatol',
      'Dermatol Surg',
    ],
  },
  {
    specialtyId: 'ai_in_medicine',
    label: 'AI in Medicine Quick Start',
    description: 'Clinical AI + generative AI essentials',
    subspecialtyIds: ['ai_in_medicine_general_core', 'ai_in_medicine_generative_ai_and_clinical_documentation'],
    topics: [
      'Clinical Decision Support',
      'Predictive Risk Modeling',
      'Generative AI for Documentation',
      'Ambient Scribing',
      'Bias, Fairness, and Health Equity',
    ],
    journals: [
      'Lancet Digit Health',
      'npj Digit Med',
      'JAMIA',
      'NEJM AI',
      'Nat Med',
    ],
  },
];

/**
 * Get the starter pack for a specialty, or undefined if none exists.
 */
export function getStarterPack(specialtyId: string): StarterPack | undefined {
  return STARTER_PACKS.find(p => p.specialtyId === specialtyId);
}

/**
 * Check if a specialty has a starter pack available.
 */
export function hasStarterPack(specialtyId: string): boolean {
  return STARTER_PACKS.some(p => p.specialtyId === specialtyId);
}
